import React, { ReactNode, useEffect, useRef } from 'react'
import { cn } from 'cn'
import { createRoot } from 'react-dom/client'
import { Mention } from 'shared/components/editor/mention'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useHighlightSearchContext } from 'modules/community/context/highlight-search'
import { getTextAroundWord } from 'modules/community/utils/get-text-around-word'
import { parseTextWithMentions } from './parse-text-with-mentions'

interface TextWithMentionsProps extends React.HTMLProps<HTMLDivElement> {
  text: string
  mentions: MentionInterface[]
  truncateLength?: number
}

export const TextWithMentions = ({
  text,
  mentions,
  truncateLength,
  ...rest
}: TextWithMentionsProps): ReactNode => {
  const containerRef = useRef<HTMLDivElement>(null)
  const highlightContext = useHighlightSearchContext()

  useEffect(() => {
    if (containerRef.current) {
      let finalHtml = parseTextWithMentions(text, mentions)
      if (truncateLength) {
        if (highlightContext?.highlightKeyword) {
          finalHtml = highlightContext.wrapWithHighlight(finalHtml, truncateLength)
        } else {
          const measure = document.createElement('div')
          measure.innerHTML = finalHtml
          const textLen = measure.textContent?.length ?? 0
          if (textLen > truncateLength) {
            finalHtml = getTextAroundWord(finalHtml, truncateLength)
          }
        }
      }

      containerRef.current.innerHTML = finalHtml

      const mentionElements =
        containerRef.current.querySelectorAll<HTMLSpanElement>('[data-type="mention"]')
      mentionElements?.forEach(element => {
        const id = element.dataset.id
        const displayName = element.dataset.cy?.split('mention-').pop()
        const profileImageUrl = element.dataset.avatarUrl

        const root = createRoot(element)
        root.render(
          <Mention
            element={{
              displayName: displayName || '',
              id: Number(id) || 0,
              profileImageUrl: profileImageUrl || '',
            }}
          />,
        )
      })
    }
  }, [
    text,
    mentions,
    truncateLength,
    highlightContext?.highlightKeyword,
    highlightContext?.wrapWithHighlight,
  ])

  return (
    <div
      ref={containerRef}
      {...rest}
      dir="auto"
      className={cn(
        'text-start [&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:list-disc [&_ul]:ps-5',
        rest.className,
      )}
    />
  )
}
